import { useEffect } from 'react';
import { REQUEST_LOADING_STATES } from '../helpers/constant';

export type UseListPagingParams = {
  listRef: React.RefObject<HTMLDivElement | null>;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  itemsLength: number;
  page: number;
  totalPages: number;
  pageSize?: number;
  loadingState: REQUEST_LOADING_STATES | string;
  lastRequestedPageRef: React.MutableRefObject<number>;
  canRun?: boolean;
  onLoadNext: (nextPage: number) => void;
};

export function useListPaging({
  listRef,
  sentinelRef,
  itemsLength,
  page,
  totalPages,
  pageSize = 15,
  loadingState,
  lastRequestedPageRef,
  canRun = true,
  onLoadNext,
}: UseListPagingParams) {
  useEffect(() => {
    if (!canRun) return;
    const rootEl = listRef.current;
    const sentinel = sentinelRef.current;
    if (!rootEl) return;

    const isScrollable = rootEl.scrollHeight > rootEl.clientHeight;
    const isLoading = loadingState === REQUEST_LOADING_STATES.PENDING;
    const hasMore = totalPages > 0 ? page < totalPages : (itemsLength || 0) > 0 && (itemsLength || 0) % pageSize === 0;

    if (!isScrollable) {
      if (hasMore && !isLoading) {
        const nextPage = (page || 1) + 1;
        if (lastRequestedPageRef.current < nextPage) {
          lastRequestedPageRef.current = nextPage;
          onLoadNext(nextPage);
        }
      }
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        const isLoadingNow = loadingState === REQUEST_LOADING_STATES.PENDING;
        const hasMoreNow = totalPages > 0 ? page < totalPages : (itemsLength || 0) > 0 && (itemsLength || 0) % pageSize === 0;
        if (!first.isIntersecting || !hasMoreNow || isLoadingNow) return;
        const nextPage = (page || 1) + 1;
        if (lastRequestedPageRef.current >= nextPage) return;
        lastRequestedPageRef.current = nextPage;
        onLoadNext(nextPage);
      },
      { root: rootEl, rootMargin: '0px 0px 400px 0px', threshold: 0 }
    );

    if (sentinel) observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canRun, listRef, sentinelRef, itemsLength, page, totalPages, pageSize, loadingState, onLoadNext, lastRequestedPageRef]);
}
